import { Autocomplete, TextField, useTheme } from '@mui/material'
import { debounce, lowerCase } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetAllCitiesQuery } from 'src/_api/mapitShipApi'


export default function MapitShipCitiesAutocomplete({ name, value, onChange, error, required, label, ...props }) {
    const { t } = useTranslation()
    const theme = useTheme()

    const { data: cities, isLoading , isFetching } = useGetAllCitiesQuery()

    const [searchKey, setSearchKey] = useState('')

    const debouncedSearch = useMemo(
        () => debounce((text) => setSearchKey(text ?? ''), 400),
        []
    )

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch])

    const searchTextHandler = useCallback((event, text) => {
        debouncedSearch(text ?? '')
    }, [debouncedSearch])

    const normalizedSearchKey = useMemo(
        () => lowerCase(searchKey?.trim() ?? ''),
        [searchKey]
    )

    const options = useMemo(() => {
        if (!normalizedSearchKey) {
            return cities ?? []
        }

        return (cities ?? []).filter(({ name_en = '', name_ar = '' }) => {
            const normalizedEn = lowerCase(name_en)
            const normalizedAr = lowerCase(name_ar)
            return normalizedEn.includes(normalizedSearchKey) || normalizedAr.includes(normalizedSearchKey)
        })
    }, [cities, normalizedSearchKey])

    const passthroughFilter = useCallback((opts) => opts, [])

    const selectedCity = useMemo(() => {
        if (!value) {
            return null
        }

        const normalizedValue = lowerCase(value)
        return (cities ?? []).find((el) => lowerCase(el?.name_en ?? '') === normalizedValue) ?? null
    }, [cities, value])

    return (
        <Autocomplete
            filterOptions={passthroughFilter} // disable built-in filtering
            isOptionEqualToValue={(option, val) => lowerCase(option?.name_en ?? '') === lowerCase(val?.name_en ?? val ?? '')}
            fullWidth
            getOptionLabel={(option) => (theme?.direction === "rtl" ? option?.name_ar : option?.name_en) ?? ""}
            onChange={(event, newValue) => onChange({ target: { name, value: newValue?.name_en ?? null } })}
            value={selectedCity}
            size="small"
            loading={isLoading || isFetching}
            loadingText={t("loading data...")}
            options={options ?? []}
            renderInput={(params) => <TextField {...params}
                label={label ?? t("City")}
                error={!!error}
                required={required}
                helperText={error ?? ''}
            />}
            onInputChange={searchTextHandler}
            slotProps={{
                paper: {
                    key: searchKey, // to force re-render and update options when searchKey changes
                },
            }}
            {...props}
        />
        // <ServerAutoCompleteSearch
        //     hasNextPage={cities?.hasNextPage}
        //     onLoadMore={() => setParams((prev) => ({ ...prev, page: prev.page + 1 }))}
        //     label={label ?? t("City")}
        //     isOptionEqualToValue={(option, value) => lowerCase(option?.name_en) === lowerCase(value)}
        //     getOptionLabel={(option) => (theme?.direction === "rtl" ? option?.name_ar : option?.name_en) ?? ""}
        //     onInputChange={(value) => {
        //         setCities([])
        //         setParams({ page: 1, limit: 20, searchKey: value })
        //     }}
        //     onChange={(newValue) => {
        //         onChange({ target: { name, value: newValue?.name_en ?? null } })
        //     }}
        //     value={cities?.docs?.find(el => lowerCase(el?.name_en) === lowerCase(value)) ?? ''}
        //     loading={isLoading}
        //     options={cities?.docs ?? []}
        //     error={error}
        //     required={required}
        //     fullWidth
        //     size="small"
        //     {...props}
        // />
    )
}
